import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import FiltersContext from '@/context/FiltersContext/FiltersContext';
import { UserFilterApiService } from '@/api/userProfile/UserFilterApiService';
import { AppliedFilterSetStorage } from '@/utils/filter/appliedFilterSetStorage';
import fetchAndApplyFilter from '@/utils/filter/fetchAndApplyFilter';
import { SHARE_VIEW_PARAM } from '@/utils/filter/shareView';
import { EXTERNAL_FILTER_PARAM } from '@/utils/filter/externalFilters';

const DEFAULT_FILTER_APPLIED_KEY = 'DEFAULT_FILTER_APPLIED';

interface UseApplyDefaultFilterResult {
  isLoading: boolean;
}

const useApplyDefaultFilter = (): UseApplyDefaultFilterResult => {
  const { handleApplyFilter, filterConfig, replaceOptionPaths } = useContext(FiltersContext);
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const attempted = useRef(false);

  const applyDefaultFilter = useCallback(async () => {
    try {
      const { data: listResponse } = await UserFilterApiService.getFilters();
      const defaultFilter = listResponse.filters.find(savedFilter => savedFilter.isDefault);
      if (!defaultFilter) return;

      setIsLoading(true);

      await fetchAndApplyFilter({
        filterId: defaultFilter.userFilterId,
        filterMetadata: defaultFilter,
        filterConfig,
        handleApplyFilter,
        replaceOptionPaths,
      });

      sessionStorage.setItem(DEFAULT_FILTER_APPLIED_KEY, 'true');
    } catch {
      // Non-critical: user continues with standard default filters.
      // Failures here are typically network issues or missing filter data.
    } finally {
      setIsLoading(false);
    }
  }, [filterConfig, handleApplyFilter, replaceOptionPaths]);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    const hasShareView = searchParams.has(SHARE_VIEW_PARAM);
    const hasExternalFilters = searchParams.has(EXTERNAL_FILTER_PARAM);
    const hasAppliedSet = AppliedFilterSetStorage.get() !== null;
    const alreadyAppliedDefault = sessionStorage.getItem(DEFAULT_FILTER_APPLIED_KEY) === 'true';

    if (hasShareView || hasExternalFilters || hasAppliedSet || alreadyAppliedDefault) return;

    applyDefaultFilter();
  }, [searchParams, applyDefaultFilter]);

  return { isLoading };
};

export default useApplyDefaultFilter;
